import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import PhoneInputAdmin from "@/components/PhoneInputAdmin";
import { format } from "date-fns";
import { 
  Luggage, 
  LayoutDashboard, 
  CalendarDays, 
  LogOut,
  Euro,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageCircle,
  Mail,
  Phone,
  ExternalLink,
  RefreshCw,
  Image,
  Trash2,
  Settings,
  Globe,
  Plus,
  Printer,
  CalendarIcon,
  UserPlus,
  Scissors,
  Edit,
  TrendingUp,
  Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800"
};

const STATUS_ICONS = {
  pending: AlertCircle,
  confirmed: CheckCircle,
  completed: CheckCircle,
  cancelled: XCircle
};

const PAYMENT_STYLES = {
  paid: "bg-green-100 text-green-800",
  due: "bg-red-100 text-red-800",
  pay_at_location: "bg-gray-100 text-gray-800"
};

const generateTimeOptions = () => {
  const times = [];
  for (let h = 6; h <= 22; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  return times;
};

const TIME_OPTIONS = generateTimeOptions();

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [adminInfo, setAdminInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState("all_time");
  
  // Physical booking states
  const [showPhysicalBooking, setShowPhysicalBooking] = useState(false);
  const [physicalFormData, setPhysicalFormData] = useState({
    full_name: "",
    mobile_number: "",
    whatsapp_number: "",
    email: "",
    number_of_bags: 1,
    pickup_date: null,
    pickup_time: "",
    dropoff_date: null,
    dropoff_time: "",
    pricing_type: "auto",
    payment_status: "due"
  });
  const [physicalPriceInfo, setPhysicalPriceInfo] = useState(null);
  const [isCalculatingPhysical, setIsCalculatingPhysical] = useState(false);
  const [isSubmittingPhysical, setIsSubmittingPhysical] = useState(false);
  
  // Receipt printing states
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);
  
  // Edit booking states
  const [showEditBooking, setShowEditBooking] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const info = localStorage.getItem("admin_info");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }
    
    if (info) {
      setAdminInfo(JSON.parse(info));
    }
    
    fetchData();
    fetchLogo();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeader()),
        axios.get(`${API}/admin/bookings`, getAuthHeader())
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_info");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      if (response.data.logo_url) {
        setLogoUrl(`${API}/logo?t=${Date.now()}`);
      }
    } catch (error) {
      console.log("No logo configured");
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      await axios.post(`${API}/admin/upload-logo`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(t('admin.dashboard.logoUploaded'));
      setLogoUrl(`${API}/logo?t=${Date.now()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/admin/logo`, getAuthHeader());
      toast.success(t('admin.dashboard.logoDeleted'));
      setLogoUrl(null);
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      toast.success(`Booking ${newStatus}`);
      fetchData();
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, booking_status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updatePaymentStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/payment`,
        { payment_status: newStatus },
        getAuthHeader()
      );
      toast.success(`Payment marked as ${newStatus}`);
      fetchData();
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, payment_status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  // Physical Booking Functions
  const calculatePhysicalPrice = async () => {
    if (!physicalFormData.pickup_date || !physicalFormData.pickup_time || 
        !physicalFormData.dropoff_date || !physicalFormData.dropoff_time) {
      return;
    }
    
    setIsCalculatingPhysical(true);
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        pickup_date: format(physicalFormData.pickup_date, "yyyy-MM-dd"),
        pickup_time: physicalFormData.pickup_time,
        dropoff_date: format(physicalFormData.dropoff_date, "yyyy-MM-dd"),
        dropoff_time: physicalFormData.dropoff_time,
        number_of_bags: physicalFormData.number_of_bags,
        pricing_type: physicalFormData.pricing_type
      });
      setPhysicalPriceInfo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate price");
      setPhysicalPriceInfo(null);
    } finally {
      setIsCalculatingPhysical(false);
    }
  };

  useEffect(() => {
    if (physicalFormData.pickup_date && physicalFormData.pickup_time && 
        physicalFormData.dropoff_date && physicalFormData.dropoff_time && 
        physicalFormData.number_of_bags > 0) {
      calculatePhysicalPrice();
    }
  }, [physicalFormData.pickup_date, physicalFormData.pickup_time, 
      physicalFormData.dropoff_date, physicalFormData.dropoff_time, 
      physicalFormData.number_of_bags, physicalFormData.pricing_type]);

  const handlePhysicalBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!physicalFormData.pickup_date || !physicalFormData.dropoff_date || 
        !physicalFormData.pickup_time || !physicalFormData.dropoff_time) {
      toast.error("Please fill all date and time fields");
      return;
    }
    
    if (!physicalFormData.full_name || !physicalFormData.mobile_number || !physicalFormData.whatsapp_number) {
      toast.error("Please fill customer details");
      return;
    }
    
    setIsSubmittingPhysical(true);
    try {
      const response = await axios.post(`${API}/admin/physical-booking`, {
        ...physicalFormData,
        pickup_date: format(physicalFormData.pickup_date, "yyyy-MM-dd"),
        dropoff_date: format(physicalFormData.dropoff_date, "yyyy-MM-dd")
      }, getAuthHeader());
      
      toast.success("Physical booking created successfully!");
      
      // Reset form and close modal
      setPhysicalFormData({
        full_name: "",
        mobile_number: "",
        whatsapp_number: "",
        email: "",
        number_of_bags: 1,
        pickup_date: null,
        pickup_time: "",
        dropoff_date: null,
        dropoff_time: "",
        pricing_type: "auto",
        payment_status: "due"
      });
      setPhysicalPriceInfo(null);
      setShowPhysicalBooking(false);
      fetchData();
      
      // Return the response for WhatsApp handling
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
      return null;
    } finally {
      setIsSubmittingPhysical(false);
    }
  };
  
  // Create booking without WhatsApp
  const handleCreateBookingOnly = async (e) => {
    e.preventDefault();
    await handlePhysicalBookingSubmit(e);
  };
  
  // Create booking and send WhatsApp
  const handleCreateAndWhatsApp = async (e) => {
    e.preventDefault();
    
    if (!physicalFormData.pickup_date || !physicalFormData.dropoff_date || 
        !physicalFormData.pickup_time || !physicalFormData.dropoff_time) {
      toast.error("Please fill all date and time fields");
      return;
    }
    
    if (!physicalFormData.full_name || !physicalFormData.mobile_number || !physicalFormData.whatsapp_number) {
      toast.error("Please fill customer details");
      return;
    }
    
    setIsSubmittingPhysical(true);
    try {
      const response = await axios.post(`${API}/admin/physical-booking`, {
        ...physicalFormData,
        pickup_date: format(physicalFormData.pickup_date, "yyyy-MM-dd"),
        dropoff_date: format(physicalFormData.dropoff_date, "yyyy-MM-dd")
      }, getAuthHeader());
      
      toast.success("Physical booking created successfully!");
      
      // Open WhatsApp link
      if (response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
      }
      
      // Reset form
      setPhysicalFormData({
        full_name: "",
        mobile_number: "",
        whatsapp_number: "",
        email: "",
        number_of_bags: 1,
        pickup_date: null,
        pickup_time: "",
        dropoff_date: null,
        dropoff_time: "",
        pricing_type: "auto",
        payment_status: "due"
      });
      setPhysicalPriceInfo(null);
      setShowPhysicalBooking(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setIsSubmittingPhysical(false);
    }
  };
  
  // Edit Booking Functions
  const openEditBooking = (booking) => {
    setEditingBooking(booking);
    setEditFormData({
      full_name: booking.full_name,
      email: booking.email || "",
      mobile_number: booking.mobile_number,
      whatsapp_number: booking.whatsapp_number || "",
      number_of_bags: booking.number_of_bags,
      pickup_date: booking.pickup_date ? new Date(booking.pickup_date) : null,
      pickup_time: booking.pickup_time,
      dropoff_date: booking.dropoff_date ? new Date(booking.dropoff_date) : null,
      dropoff_time: booking.dropoff_time,
      pricing_type: booking.pricing_type || "auto",
      payment_status: booking.payment_status || "due",
      booking_status: booking.booking_status || "pending",
      total_price: booking.total_price
    });
    setShowEditBooking(true);
    setSelectedBooking(null);
  };
  
  const handleEditBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingEdit(true);
    
    try {
      const updateData = {
        ...editFormData,
        pickup_date: editFormData.pickup_date ? format(editFormData.pickup_date, "yyyy-MM-dd") : undefined,
        dropoff_date: editFormData.dropoff_date ? format(editFormData.dropoff_date, "yyyy-MM-dd") : undefined
      };
      
      await axios.put(`${API}/admin/bookings/${editingBooking.id}`, updateData, getAuthHeader());
      toast.success("Booking updated successfully!");
      setShowEditBooking(false);
      setEditingBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    } finally {
      setIsSubmittingEdit(false);
    }
  };
  
  // Update booking status with Google review link for completed
  const updateBookingStatusWithReview = async (bookingId, newStatus) => {
    try {
      const response = await axios.patch(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      toast.success(`Booking ${newStatus}`);
      fetchData();
      
      // If completed, open Google review WhatsApp
      if (newStatus === "completed" && response.data.google_review_whatsapp_link) {
        if (window.confirm("Booking completed! Would you like to send Google Review request to customer?")) {
          window.open(response.data.google_review_whatsapp_link, '_blank');
        }
      }
      
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking({ ...selectedBooking, booking_status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Receipt Functions
  const fetchReceiptData = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/receipt`, getAuthHeader());
      setReceiptData(response.data);
      setShowReceipt(true);
    } catch (error) {
      toast.error("Failed to fetch receipt data");
    }
  };

  const handlePrint = () => {
    const printContent = receiptRef.current;
    const printWindow = window.open('', '', 'width=300,height=500');
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt - ${receiptData?.booking?.booking_number || ''}</title>
          <style>
            @page { size: 58mm auto; margin: 0; }
            * { box-sizing: border-box; }
            body { 
              font-family: 'Courier New', Courier, monospace; 
              padding: 2mm; 
              margin: 0; 
              width: 58mm;
              font-size: 10px;
              line-height: 1.2;
            }
            .receipt { width: 100%; }
            .header { 
              text-align: center; 
              border-bottom: 1px dashed #000; 
              padding-bottom: 3mm; 
              margin-bottom: 2mm; 
            }
            .header h1 { font-size: 12px; margin: 0 0 1mm 0; font-weight: bold; }
            .header p { font-size: 8px; margin: 1mm 0; }
            .section { 
              margin: 2mm 0; 
              padding: 2mm 0; 
              border-bottom: 1px dashed #000; 
            }
            .row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1mm 0; 
              font-size: 9px; 
            }
            .label { color: #333; }
            .value { font-weight: bold; text-align: right; }
            .total { 
              text-align: center; 
              margin: 3mm 0; 
              padding: 2mm 0;
            }
            .total .label { font-size: 10px; }
            .total .amount { font-size: 18px; font-weight: bold; display: block; margin-top: 1mm; }
            .status { 
              text-align: center; 
              padding: 2mm; 
              margin: 2mm 0; 
              border: 1px solid #000; 
              font-weight: bold; 
              font-size: 10px;
            }
            .status.paid { background: #d4edda; }
            .status.due { background: #f8d7da; }
            .cut-line { 
              border-top: 1px dashed #000; 
              margin: 4mm 0 2mm 0; 
              text-align: center; 
              font-size: 8px; 
              padding-top: 2mm; 
            }
            .token { 
              text-align: center; 
              padding: 3mm; 
              border: 2px solid #000; 
              margin-top: 2mm; 
            }
            .token h2 { font-size: 16px; margin: 0; letter-spacing: 1px; font-weight: bold; }
            .token p { margin: 1mm 0; font-size: 9px; }
            @media print { 
              body { width: 58mm; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const sendWhatsAppNotification = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/whatsapp-link`, getAuthHeader());
      window.open(response.data.whatsapp_link, '_blank');
    } catch (error) {
      toast.error("Failed to generate WhatsApp link");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_info");
    navigate("/admin/login");
  };

  const filteredBookings = statusFilter === "all" 
    ? bookings 
    : bookings.filter(b => b.booking_status === statusFilter);

  const fetchBookingDetails = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}`, getAuthHeader());
      setSelectedBooking(response.data);
    } catch (error) {
      toast.error("Failed to fetch booking details");
    }
  };
  
  // Get stats for selected period
  const getPeriodStats = () => {
    if (!stats) return { bookings: 0, revenue: 0 };
    switch (statsPeriod) {
      case "today": return stats.today || { bookings: 0, revenue: 0 };
      case "this_month": return stats.this_month || { bookings: 0, revenue: 0 };
      case "this_year": return stats.this_year || { bookings: 0, revenue: 0 };
      default: return stats.all_time || { bookings: stats.total_bookings, revenue: stats.total_revenue };
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4 w-12 h-12 border-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-[#004E64] text-white z-40 hidden md:block">
        <div className="p-6 border-b border-white/10">
          <div className="flex flex-col items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-12 w-auto object-contain" />
            ) : (
              <Luggage className="w-8 h-8 text-[#FFB400]" />
            )}
            <span className="font-serif text-sm font-semibold text-center">{t('admin.login.portal')}</span>
          </div>
        </div>
        
        <nav className="p-4">
          <button 
            onClick={() => setActiveTab("dashboard")}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left ${
              activeTab === "dashboard" ? "bg-[#FFB400] text-[#004E64]" : "text-white/70 hover:bg-white/10"
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            {t('admin.dashboard.title')}
          </button>
          <button 
            onClick={() => setActiveTab("bookings")}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left ${
              activeTab === "bookings" ? "bg-[#FFB400] text-[#004E64]" : "text-white/70 hover:bg-white/10"
            }`}
          >
            <CalendarDays className="w-5 h-5" />
            {t('admin.dashboard.bookings')}
          </button>
          <button 
            onClick={() => setShowPhysicalBooking(true)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left bg-green-500 hover:bg-green-600 text-white"
            data-testid="physical-booking-btn"
          >
            <UserPlus className="w-5 h-5" />
            Walk-in Booking
          </button>
          <button 
            onClick={() => setActiveTab("settings")}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left ${
              activeTab === "settings" ? "bg-[#FFB400] text-[#004E64]" : "text-white/70 hover:bg-white/10"
            }`}
            data-testid="settings-tab"
          >
            <Settings className="w-5 h-5" />
            {t('admin.dashboard.settings')}
          </button>
          <button 
            onClick={() => navigate("/admin/settings")}
            className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left text-white/70 hover:bg-white/10"
            data-testid="site-settings-tab"
          >
            <Globe className="w-5 h-5" />
            Site Settings
          </button>
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="mb-4 px-4">
            <p className="text-sm text-white/50">{t('admin.dashboard.loggedInAs')}</p>
            <p className="text-white font-medium truncate">{adminInfo?.name || adminInfo?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-white/10 rounded-xl transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {t('admin.dashboard.logout')}
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#004E64] text-white p-4 z-40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain" />
          ) : (
            <Luggage className="w-6 h-6 text-[#FFB400]" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <LanguageSelector variant="dark" />
          <button onClick={handleLogout} className="p-2" data-testid="mobile-logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:ml-64 p-6 pt-20 md:pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold text-[#004E64]">
              {activeTab === "dashboard" && t('admin.dashboard.title')}
              {activeTab === "bookings" && t('admin.dashboard.bookings')}
              {activeTab === "settings" && t('admin.dashboard.settings')}
            </h1>
            <p className="text-slate-500">
              {activeTab === "dashboard" && t('admin.dashboard.overview')}
              {activeTab === "bookings" && t('admin.dashboard.manageBookings')}
              {activeTab === "settings" && t('admin.dashboard.configureSettings')}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
            {activeTab !== "settings" && (
              <Button
                onClick={fetchData}
                variant="outline"
                className="border-[#004E64]/20 hover:bg-[#004E64] hover:text-white"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('admin.dashboard.refresh')}
              </Button>
            )}
          </div>
        </div>

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="font-sans text-lg font-bold text-[#004E64] mb-6 flex items-center gap-2">
              <Image className="w-5 h-5" /> {t('admin.dashboard.companyLogo')}
            </h2>
            
            <div className="max-w-md">
              <div className="mb-6">
                <p className="text-sm text-slate-500 mb-3">{t('admin.dashboard.currentLogo')}</p>
                <div className="w-32 h-32 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Company Logo" className="max-w-full max-h-full object-contain p-2" />
                  ) : (
                    <div className="text-center text-slate-400">
                      <Luggage className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-xs">{t('admin.dashboard.noLogo')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block">
                    <span className="sr-only">Choose logo file</span>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#004E64] file:text-white hover:file:bg-[#003848] cursor-pointer"
                      data-testid="logo-upload-input"
                    />
                  </label>
                  <p className="text-xs text-slate-400 mt-2">{t('admin.dashboard.uploadInfo')}</p>
                </div>

                {logoUrl && (
                  <Button
                    onClick={handleDeleteLogo}
                    variant="outline"
                    className="text-red-500 border-red-200 hover:bg-red-50"
                    data-testid="delete-logo-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('admin.dashboard.removeLogo')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid - Dashboard Tab */}
        {activeTab === "dashboard" && stats && (
          <>
            {/* Period Filter Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { key: "today", label: "Today", icon: CalendarDays, color: "bg-blue-500" },
                { key: "this_month", label: "This Month", icon: CalendarDays, color: "bg-purple-500" },
                { key: "this_year", label: "This Year", icon: TrendingUp, color: "bg-green-500" },
                { key: "all_time", label: "All Time", icon: Euro, color: "bg-[#004E64]" }
              ].map((period) => {
                const periodStats = stats[period.key] || { bookings: 0, revenue: 0 };
                const isActive = statsPeriod === period.key;
                return (
                  <button
                    key={period.key}
                    onClick={() => setStatsPeriod(period.key)}
                    className={`rounded-2xl p-4 shadow-sm transition-all text-left ${
                      isActive 
                        ? `${period.color} text-white scale-105` 
                        : 'bg-white hover:shadow-md'
                    }`}
                    data-testid={`period-${period.key}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <period.icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                      <span className={`text-xs font-bold uppercase tracking-wider ${isActive ? 'text-white/80' : 'text-slate-500'}`}>
                        {period.label}
                      </span>
                    </div>
                    <p className={`text-2xl font-bold ${isActive ? 'text-white' : 'text-[#004E64]'}`}>
                      {periodStats.bookings} <span className="text-sm font-normal">bookings</span>
                    </p>
                    <p className={`text-lg font-semibold ${isActive ? 'text-white/90' : 'text-green-600'}`}>
                      €{periodStats.revenue}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Status Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#004E64]/10 rounded-xl flex items-center justify-center">
                    <CalendarDays className="w-5 h-5 text-[#004E64]" />
                  </div>
                  <span className="text-sm text-slate-500">{t('admin.dashboard.totalBookings')}</span>
                </div>
                <p className="font-serif text-3xl font-bold text-[#004E64]" data-testid="stat-total">{stats.total_bookings}</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="text-sm text-slate-500">{t('admin.dashboard.pending')}</span>
                </div>
                <p className="font-serif text-3xl font-bold text-yellow-600" data-testid="stat-pending">{stats.pending_bookings}</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm text-slate-500">{t('admin.dashboard.confirmed')}</span>
                </div>
                <p className="font-serif text-3xl font-bold text-green-600" data-testid="stat-confirmed">{stats.confirmed_bookings}</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm text-slate-500">{t('admin.dashboard.completed') || 'Completed'}</span>
                </div>
                <p className="font-serif text-3xl font-bold text-blue-600" data-testid="stat-completed">{stats.completed_bookings}</p>
              </div>
            </div>

            {/* Recent Bookings Preview */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-sans text-lg font-bold text-[#004E64]">{t('admin.dashboard.recentBookings')}</h2>
                <Button variant="ghost" onClick={() => setActiveTab("bookings")} className="text-[#004E64]">
                  {t('admin.dashboard.viewAll')}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.customer')}</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('booking.bags')}</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('confirmation.pickup')}</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.price')}</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.slice(0, 5).map((booking) => {
                      const StatusIcon = STATUS_ICONS[booking.booking_status];
                      return (
                        <TableRow key={booking.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => fetchBookingDetails(booking.id)}>
                          <TableCell>
                            <p className="font-medium text-[#004E64]">{booking.full_name}</p>
                          </TableCell>
                          <TableCell>{booking.number_of_bags}</TableCell>
                          <TableCell>{booking.pickup_date}</TableCell>
                          <TableCell className="font-serif font-bold text-[#004E64]">€{booking.total_price}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase ${STATUS_STYLES[booking.booking_status]}`}>
                              <StatusIcon className="w-3 h-3" />
                              {booking.booking_status}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}

        {/* Bookings Table - Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-sans text-lg font-bold text-[#004E64]">{t('admin.dashboard.allBookings')}</h2>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder={t('admin.dashboard.filterStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.dashboard.all')}</SelectItem>
                  <SelectItem value="pending">{t('admin.dashboard.pending')}</SelectItem>
                  <SelectItem value="confirmed">{t('admin.dashboard.confirmed')}</SelectItem>
                  <SelectItem value="completed">{t('admin.dashboard.completed')}</SelectItem>
                  <SelectItem value="cancelled">{t('admin.dashboard.cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.customer')}</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('booking.bags')}</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('confirmation.pickup')}</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('confirmation.dropoff')}</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.price')}</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.status')}</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-[#004E64]/70">{t('admin.dashboard.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        {t('admin.dashboard.noBookings')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBookings.map((booking) => {
                      const StatusIcon = STATUS_ICONS[booking.booking_status];
                      return (
                        <TableRow key={booking.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => fetchBookingDetails(booking.id)}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#004E64]" data-testid={`booking-name-${booking.id}`}>{booking.full_name}</p>
                              <p className="text-sm text-slate-500">{booking.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{booking.number_of_bags}</span>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{booking.pickup_date}</p>
                              <p className="text-sm text-slate-500">{booking.pickup_time}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{booking.dropoff_date}</p>
                              <p className="text-sm text-slate-500">{booking.dropoff_time}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-serif font-bold text-[#004E64]">€{booking.total_price}</span>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase ${STATUS_STYLES[booking.booking_status]}`} data-testid={`booking-status-${booking.id}`}>
                              <StatusIcon className="w-3 h-3" />
                              {booking.booking_status}
                            </span>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <Select
                                value={booking.booking_status}
                                onValueChange={(value) => updateBookingStatusWithReview(booking.id, value)}
                              >
                                <SelectTrigger className="w-[100px] h-8 text-xs" data-testid={`update-status-${booking.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">{t('admin.dashboard.pending')}</SelectItem>
                                  <SelectItem value="confirmed">{t('admin.dashboard.confirm')}</SelectItem>
                                  <SelectItem value="completed">{t('admin.dashboard.complete')}</SelectItem>
                                  <SelectItem value="cancelled">{t('admin.dashboard.cancel')}</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditBooking(booking)}
                                className="h-8 w-8 p-0"
                                data-testid={`edit-booking-${booking.id}`}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fetchReceiptData(booking.id)}
                                className="h-8 w-8 p-0"
                                data-testid={`print-booking-${booking.id}`}
                              >
                                <Printer className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#004E64]">{t('admin.dashboard.bookingDetails')}</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-3">{t('admin.dashboard.customer')}</h4>
                <div className="space-y-2">
                  <p className="font-semibold text-lg text-[#004E64]">{selectedBooking.full_name}</p>
                  <p className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" /> {selectedBooking.email}
                  </p>
                  <p className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" /> {selectedBooking.mobile_number}
                  </p>
                  <a 
                    href={selectedBooking.whatsapp_customer_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-600 hover:text-green-700"
                    data-testid="modal-whatsapp-link"
                  >
                    <MessageCircle className="w-4 h-4" /> 
                    {t('admin.dashboard.contactWhatsApp')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-3">{t('admin.dashboard.bookings')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">{t('booking.bags')}</p>
                    <p className="font-semibold">{selectedBooking.number_of_bags}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('confirmation.pricingType')}</p>
                    <p className="font-semibold capitalize">{selectedBooking.pricing_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('confirmation.pickup')}</p>
                    <p className="font-semibold">{selectedBooking.pickup_date} @ {selectedBooking.pickup_time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('confirmation.dropoff')}</p>
                    <p className="font-semibold">{selectedBooking.dropoff_date} @ {selectedBooking.dropoff_time}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="bg-[#004E64] rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-sm">{t('confirmation.totalPrice')}</p>
                    <p className={`text-sm font-bold ${selectedBooking.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {selectedBooking.payment_status === 'paid' ? '✓ PAID' : '⏳ ' + (selectedBooking.payment_status === 'due' ? 'DUE' : 'Pay at Location')}
                    </p>
                  </div>
                  <p className="font-serif text-3xl font-bold text-[#FFB400]">€{selectedBooking.total_price}</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-3">Payment Status</h4>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Button
                    variant={selectedBooking.payment_status === 'paid' ? "default" : "outline"}
                    size="sm"
                    onClick={() => updatePaymentStatus(selectedBooking.id, 'paid')}
                    className={selectedBooking.payment_status === 'paid' ? "bg-green-600 hover:bg-green-700" : ""}
                    data-testid="payment-paid"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Paid
                  </Button>
                  <Button
                    variant={selectedBooking.payment_status === 'due' ? "default" : "outline"}
                    size="sm"
                    onClick={() => updatePaymentStatus(selectedBooking.id, 'due')}
                    className={selectedBooking.payment_status === 'due' ? "bg-red-600 hover:bg-red-700" : ""}
                    data-testid="payment-due"
                  >
                    <AlertCircle className="w-4 h-4 mr-1" /> Due
                  </Button>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-3">{t('admin.dashboard.updateStatus')}</h4>
                <div className="grid grid-cols-4 gap-2">
                  {['pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedBooking.booking_status === status ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateBookingStatus(selectedBooking.id, status)}
                      className={selectedBooking.booking_status === status ? "bg-[#004E64]" : ""}
                      data-testid={`modal-status-${status}`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t flex gap-2">
                <Button
                  onClick={() => sendWhatsAppNotification(selectedBooking.id)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="send-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Send WhatsApp
                </Button>
                <Button
                  onClick={() => { fetchReceiptData(selectedBooking.id); setSelectedBooking(null); }}
                  className="flex-1 bg-[#004E64] hover:bg-[#003848]"
                  data-testid="print-receipt"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Physical Booking Modal */}
      <Dialog open={showPhysicalBooking} onOpenChange={setShowPhysicalBooking}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#004E64] flex items-center gap-2">
              <UserPlus className="w-6 h-6" /> Walk-in Booking
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handlePhysicalBookingSubmit} className="space-y-6">
            {/* Customer Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                <Phone className="w-4 h-4" /> Customer Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Full Name *</Label>
                  <Input
                    value={physicalFormData.full_name}
                    onChange={(e) => setPhysicalFormData({...physicalFormData, full_name: e.target.value})}
                    placeholder="Customer name"
                    required
                    data-testid="physical-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Email</Label>
                  <Input
                    type="email"
                    value={physicalFormData.email}
                    onChange={(e) => setPhysicalFormData({...physicalFormData, email: e.target.value})}
                    placeholder="Optional"
                    data-testid="physical-email"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Mobile Number *</Label>
                  <PhoneInputAdmin
                    value={physicalFormData.mobile_number}
                    onChange={(value) => setPhysicalFormData({...physicalFormData, mobile_number: value})}
                    placeholder="333 123 4567"
                    required
                    testId="physical-mobile"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">WhatsApp Number *</Label>
                  <PhoneInputAdmin
                    value={physicalFormData.whatsapp_number}
                    onChange={(value) => setPhysicalFormData({...physicalFormData, whatsapp_number: value})}
                    placeholder="333 123 4567"
                    required
                    testId="physical-whatsapp"
                  />
                </div>
              </div>
            </div>

            {/* Storage Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                <Luggage className="w-4 h-4" /> Storage Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Number of Bags *</Label>
                  <Select
                    value={physicalFormData.number_of_bags.toString()}
                    onValueChange={(value) => setPhysicalFormData({...physicalFormData, number_of_bags: parseInt(value)})}
                  >
                    <SelectTrigger data-testid="physical-bags">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10].map(num => (
                        <SelectItem key={num} value={num.toString()}>{num} bag{num > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Pricing Type</Label>
                  <Select
                    value={physicalFormData.pricing_type}
                    onValueChange={(value) => setPhysicalFormData({...physicalFormData, pricing_type: value})}
                  >
                    <SelectTrigger data-testid="physical-pricing">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (Best Rate)</SelectItem>
                      <SelectItem value="hourly">Hourly (€1.5/hr)</SelectItem>
                      <SelectItem value="daily">Daily (€5/day)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium">Drop-off Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="physical-dropoff-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {physicalFormData.dropoff_date ? format(physicalFormData.dropoff_date, "MMM d") : "Select"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={physicalFormData.dropoff_date}
                        onSelect={(date) => setPhysicalFormData({...physicalFormData, dropoff_date: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-sm font-medium">Drop-off Time *</Label>
                  <Select
                    value={physicalFormData.dropoff_time}
                    onValueChange={(value) => setPhysicalFormData({...physicalFormData, dropoff_time: value})}
                  >
                    <SelectTrigger data-testid="physical-dropoff-time">
                      <SelectValue placeholder="Time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Pick-up Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="physical-pickup-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {physicalFormData.pickup_date ? format(physicalFormData.pickup_date, "MMM d") : "Select"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={physicalFormData.pickup_date}
                        onSelect={(date) => setPhysicalFormData({...physicalFormData, pickup_date: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-sm font-medium">Pick-up Time *</Label>
                  <Select
                    value={physicalFormData.pickup_time}
                    onValueChange={(value) => setPhysicalFormData({...physicalFormData, pickup_time: value})}
                  >
                    <SelectTrigger data-testid="physical-pickup-time">
                      <SelectValue placeholder="Time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                <Euro className="w-4 h-4" /> Payment
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setPhysicalFormData({...physicalFormData, payment_status: "paid"})}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    physicalFormData.payment_status === "paid"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-green-300"
                  }`}
                  data-testid="physical-paid"
                >
                  <CheckCircle className={`w-6 h-6 mx-auto mb-2 ${physicalFormData.payment_status === "paid" ? "text-green-500" : "text-gray-400"}`} />
                  <p className="font-bold">PAID</p>
                  <p className="text-xs text-gray-500">Customer paid now</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPhysicalFormData({...physicalFormData, payment_status: "due"})}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    physicalFormData.payment_status === "due"
                      ? "border-yellow-500 bg-yellow-50"
                      : "border-gray-200 hover:border-yellow-300"
                  }`}
                  data-testid="physical-due"
                >
                  <AlertCircle className={`w-6 h-6 mx-auto mb-2 ${physicalFormData.payment_status === "due" ? "text-yellow-500" : "text-gray-400"}`} />
                  <p className="font-bold">DUE</p>
                  <p className="text-xs text-gray-500">Pay at pick-up</p>
                </button>
              </div>
            </div>

            {/* Price Display */}
            {physicalPriceInfo && (
              <div className="bg-[#004E64] rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-sm">Total Price</p>
                    <p className="text-white text-sm">{physicalPriceInfo.breakdown}</p>
                    <p className="text-white/60 text-xs mt-1">
                      Type: {physicalPriceInfo.pricing_type === 'hourly' ? 'Hourly' : 'Daily'} | 
                      Duration: {physicalPriceInfo.duration}
                    </p>
                  </div>
                  <p className="font-serif text-3xl font-bold text-[#FFB400]">€{physicalPriceInfo.total_price}</p>
                </div>
              </div>
            )}

            {!physicalPriceInfo && physicalFormData.dropoff_date && physicalFormData.pickup_date && (
              <div className="bg-yellow-50 rounded-xl p-4 text-center text-yellow-700">
                Please select drop-off and pick-up times to see the price
              </div>
            )}

            {isCalculatingPhysical && (
              <div className="text-center py-2">
                <div className="spinner inline-block mr-2"></div>
                Calculating price...
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPhysicalBooking(false)}
                className="w-24"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreateBookingOnly}
                disabled={isSubmittingPhysical || !physicalPriceInfo}
                className="flex-1 bg-[#004E64] hover:bg-[#003848]"
                data-testid="physical-create-only"
              >
                {isSubmittingPhysical ? (
                  <>Creating...</>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" /> Create Booking
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={handleCreateAndWhatsApp}
                disabled={isSubmittingPhysical || !physicalPriceInfo}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="physical-submit"
              >
                {isSubmittingPhysical ? (
                  <>Creating...</>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4 mr-2" /> Create & WhatsApp
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#004E64] flex items-center gap-2">
              <Printer className="w-6 h-6" /> Print Receipt & Token
            </DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <>
              <div ref={receiptRef} className="bg-white p-4 border rounded-lg font-mono text-sm">
                <div className="receipt">
                  {/* Receipt Header */}
                  <div className="header text-center border-b-2 border-dashed border-black pb-3 mb-3">
                    <h1 className="font-bold text-lg">{receiptData.business.name}</h1>
                    <p className="text-xs">{receiptData.business.address}</p>
                    <p className="text-xs">{receiptData.business.phone}</p>
                  </div>

                  {/* Booking Info */}
                  <div className="section border-b border-dashed border-gray-400 pb-3 mb-3">
                    <div className="row flex justify-between mb-1">
                      <span className="label text-gray-600">Booking #:</span>
                      <span className="value font-bold">{receiptData.booking.booking_number || receiptData.booking.id.slice(0,8).toUpperCase()}</span>
                    </div>
                    <div className="row flex justify-between mb-1">
                      <span className="label text-gray-600">Customer:</span>
                      <span className="value font-bold">{receiptData.booking.full_name}</span>
                    </div>
                    <div className="row flex justify-between mb-1">
                      <span className="label text-gray-600">Bags:</span>
                      <span className="value font-bold">{receiptData.booking.number_of_bags}</span>
                    </div>
                  </div>

                  {/* Date/Time Info */}
                  <div className="section border-b border-dashed border-gray-400 pb-3 mb-3">
                    <div className="row flex justify-between mb-1">
                      <span className="label text-gray-600">Drop-off:</span>
                      <span className="value font-bold">{receiptData.booking.dropoff_date} {receiptData.booking.dropoff_time}</span>
                    </div>
                    <div className="row flex justify-between mb-1">
                      <span className="label text-gray-600">Pick-up:</span>
                      <span className="value font-bold">{receiptData.booking.pickup_date} {receiptData.booking.pickup_time}</span>
                    </div>
                    <div className="row flex justify-between mb-1">
                      <span className="label text-gray-600">Type:</span>
                      <span className="value font-bold capitalize">{receiptData.booking.pricing_type}</span>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="total text-center my-4">
                    <p className="text-gray-600">TOTAL</p>
                    <p className="amount text-3xl font-bold">€{receiptData.booking.total_price}</p>
                  </div>

                  {/* Payment Status */}
                  <div className={`status text-center p-2 border-2 border-black font-bold ${
                    receiptData.booking.payment_status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                  }`}>
                    {receiptData.booking.payment_status === 'paid' ? '✓ PAID' : '⏳ PAYMENT DUE'}
                  </div>

                  {/* Cut Line */}
                  <div className="cut-line border-t-2 border-dashed border-black mt-6 pt-2 text-center text-xs">
                    <Scissors className="w-4 h-4 inline-block mr-1" /> CUT HERE - ATTACH TO BAG
                  </div>

                  {/* Token */}
                  <div className="token text-center p-4 border-4 border-black mt-3">
                    <p className="text-xs text-gray-600 mb-1">🏷️ LUGGAGE TAG</p>
                    <h2 className="text-2xl font-bold tracking-wider">{receiptData.booking.booking_number || receiptData.booking.id.slice(0,8).toUpperCase()}</h2>
                    <p className="mt-2 font-bold">{receiptData.booking.full_name}</p>
                    <p className="text-xs text-gray-600 mt-1">{receiptData.booking.number_of_bags} bag(s)</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowReceipt(false)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={handlePrint}
                  className="flex-1 bg-[#004E64] hover:bg-[#003848]"
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={showEditBooking} onOpenChange={setShowEditBooking}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#004E64] flex items-center gap-2">
              <Edit className="w-6 h-6" /> Edit Booking
              {editingBooking?.booking_number && (
                <span className="text-sm bg-[#004E64]/10 px-2 py-1 rounded">{editingBooking.booking_number}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {editingBooking && (
            <form onSubmit={handleEditBookingSubmit} className="space-y-6">
              {/* Customer Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Customer Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Full Name</Label>
                    <Input
                      value={editFormData.full_name || ""}
                      onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                      data-testid="edit-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      value={editFormData.email || ""}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      data-testid="edit-email"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Mobile Number</Label>
                    <PhoneInputAdmin
                      value={editFormData.mobile_number || ""}
                      onChange={(value) => setEditFormData({...editFormData, mobile_number: value})}
                      placeholder="333 123 4567"
                      testId="edit-mobile"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">WhatsApp Number</Label>
                    <PhoneInputAdmin
                      value={editFormData.whatsapp_number || ""}
                      onChange={(value) => setEditFormData({...editFormData, whatsapp_number: value})}
                      placeholder="333 123 4567"
                      testId="edit-whatsapp"
                    />
                  </div>
                </div>
              </div>

              {/* Storage Details */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                  <Luggage className="w-4 h-4" /> Storage Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Number of Bags</Label>
                    <Select
                      value={editFormData.number_of_bags?.toString() || "1"}
                      onValueChange={(value) => setEditFormData({...editFormData, number_of_bags: parseInt(value)})}
                    >
                      <SelectTrigger data-testid="edit-bags">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10].map(num => (
                          <SelectItem key={num} value={num.toString()}>{num} bag{num > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Pricing Type</Label>
                    <Select
                      value={editFormData.pricing_type || "auto"}
                      onValueChange={(value) => setEditFormData({...editFormData, pricing_type: value})}
                    >
                      <SelectTrigger data-testid="edit-pricing">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (Best Rate)</SelectItem>
                        <SelectItem value="hourly">Hourly (€1.5/hr)</SelectItem>
                        <SelectItem value="daily">Daily (€5/day)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Drop-off Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-dropoff-date">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {editFormData.dropoff_date ? format(editFormData.dropoff_date, "MMM d, yyyy") : "Select"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={editFormData.dropoff_date}
                          onSelect={(date) => setEditFormData({...editFormData, dropoff_date: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Drop-off Time</Label>
                    <Select
                      value={editFormData.dropoff_time || ""}
                      onValueChange={(value) => setEditFormData({...editFormData, dropoff_time: value})}
                    >
                      <SelectTrigger data-testid="edit-dropoff-time">
                        <SelectValue placeholder="Time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_OPTIONS.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Pick-up Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="edit-pickup-date">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {editFormData.pickup_date ? format(editFormData.pickup_date, "MMM d, yyyy") : "Select"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={editFormData.pickup_date}
                          onSelect={(date) => setEditFormData({...editFormData, pickup_date: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Pick-up Time</Label>
                    <Select
                      value={editFormData.pickup_time || ""}
                      onValueChange={(value) => setEditFormData({...editFormData, pickup_time: value})}
                    >
                      <SelectTrigger data-testid="edit-pickup-time">
                        <SelectValue placeholder="Time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_OPTIONS.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Status & Payment */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                  <Euro className="w-4 h-4" /> Status & Payment
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Booking Status</Label>
                    <Select
                      value={editFormData.booking_status || "pending"}
                      onValueChange={(value) => setEditFormData({...editFormData, booking_status: value})}
                    >
                      <SelectTrigger data-testid="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    
                    <Label className="text-sm font-medium">Payment Status</Label>
                    <Select
                      value={editFormData.payment_status || "due"}
                      onValueChange={(value) => setEditFormData({...editFormData, payment_status: value})}
                    >
                      <SelectTrigger data-testid="edit-payment">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="due">Due</SelectItem>
                        <SelectItem value="pay_at_location">Pay at Location</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Price Edit Section */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-[#004E64] flex items-center gap-2">
                  <Euro className="w-4 h-4" /> Price
                </h3>
                
                <div className="bg-slate-100 rounded-xl p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label className="text-sm font-medium">Total Price (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editFormData.total_price ?? editingBooking.total_price}
                        onChange={(e) => setEditFormData({...editFormData, total_price: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        data-testid="edit-price"
                      />
                      <p className="text-xs text-slate-500 mt-1">Edit directly or change dates/bags to recalculate</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowEditBooking(false); setEditingBooking(null); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingEdit}
                  className="flex-1 bg-[#004E64] hover:bg-[#003848]"
                  data-testid="edit-submit"
                >
                  {isSubmittingEdit ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
